/* global window, videojs */

import get from 'lodash/get';

/**
 * Class representing an Amazon bid.
 */
class AmazonBid {
  /**
   * Create an Amazon bid.
   * @param {Object} id - player ID.
   * @param {string} settings - player settings.
   * @param {string} slotId - bid slot ID.
   */
  constructor(id, settings, slotId = 'videoSlot') {
    this.id = id;
    this.settings = settings;
    this.bids = [];
    this.timeout = 2000;
    this.waitTimeout = 2000;
    this.slotId = slotId;
  }

  /**
   * Check if Amazon A9 is loaded.
   * @returns boolean
   */
  get isA9Ready() {
    return typeof (window.apstag) !== 'undefined';
  }

  /**
   * Wait for Amazon A9 to be ready.
   * @param {function} resolve - promise resolve function.
   * @param {integer} startTime - start time.
   * @returns {Promise}
   */
  waitFunc(resolve, startTime) {
    if (this.isA9Ready) {
      resolve(true);
      return;
    }

    if (Date.now() - startTime > this.waitTimeout) {
      resolve(false);
      return;
    }

    setTimeout(this.waitFunc.bind(this, resolve, startTime), 100);
  }

  /**
   * Wait for Amazon A9 to be ready.
   * @returns {Promise}
   */
  waitForA9() {
    const startTime = Date.now();

    return new Promise(resolve => {
      this.waitFunc(resolve, startTime);
    });
  }

  /**
   * Request Amazon bid response.
   * @returns {Promise}
   */
  bid() {
    return new Promise(resolve => {
      if (!this.settings.enableAmazonBids) {
        videojs.log(`${this.id} A9 disabled`);
        resolve();
        return;
      }

      if (!this.settings.imaSdkAvailable) {
        videojs.log(`${this.id} A9 IMA SDK not available`);
        resolve();
        return;
      }

      if (!this.isA9Ready) {
        videojs.log(`${this.id} A9 not ready`);
        resolve();
        return;
      }

      const slots = [];
      slots.push({
        slotID: this.slotId,
        mediaType: 'video',
      });

      window.apstag.fetchBids({
        slots,
        timeout: this.timeout,
      }, bids => {
        videojs.log(`${this.id} A9 got bids: [${bids.length}]`);

        this.bids = bids;

        resolve();
      });
    });
  }

  /**
   * Request Amazon bid response.
   * @returns {Promise}
   */
  request() {
    if (this.isA9Ready ||
      !this.settings.imaSdkAvailable ||
      !this.settings.enableAmazonBids) {
      return this.bid();
    }

    return new Promise(resolve => {
      this.waitForA9()
        .then(() => resolve(this.bid()));
    });
  }

  /**
   * Return Amazon bid response.
   */
  get scp() {
    let scp = '';
    const encodedQsParams = get(this.bids, '[0].encodedQsParams', '');

    if (encodedQsParams.length > 0) {
      scp = encodedQsParams.substring(3, encodedQsParams.length);
    }

    return scp;
  }

  /**
   * Sets bid slot ID.
   * @param {string} slotId bid slot ID.
   */
  setSlotId(slotId) {
    this.slotId = slotId;
  }
}

export default AmazonBid;



// WEBPACK FOOTER //
// ./src/AmazonBid.js